import {useQuery} from '@tanstack/react-query';
import {queryKeys} from './query-keys';
import {UseDesignerSelector, UseDispatch} from '../_internal-shared-types';
import {
  getAPIProxyPath,
  selectCustomerId,
  selectSiteShortName,
} from '../shared';
import fetch from '@packages/utilities/fetch';
import {customerJsonToOptimizationJson} from './customerJsonToOptimizationJson';
import {
  OptimizationJson,
  OptimizationJsonSiteOptimizeAnalyze,
} from '@packages/systems/optimization/types';
import {CustomerJson} from '@intellimize/customer';
import {queryClient} from '@packages/utilities/react-query';
import * as Environment from '@packages/utilities/environment';
import {
  optimizationJsonInvalidated,
  OptimizationJsonInvalidatedAction,
  selectCurrentExperienceId,
  selectOptimizationStore,
} from '@packages/systems/optimization/store';
import {
  selectCurrentPageId,
  getPageStore,
} from '@packages/systems/designer/page';
import {getPage} from '@packages/shared/utils/PageTreeUtils';
import type {PageState} from '@packages/systems/core/records/PageRecord';
import {useEntitlements} from '@packages/systems/billing/client';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {createSelector} from 'reselect';
import {TEMPORARY_EXPERIENCE_ID} from '@packages/systems/optimization/constants';
import {createTemporaryExperience} from '../utils/createTemporaryExperience';
import {generateNewExperienceName} from '../utils/generateNewExperienceName';

export const fetchIntellimizeCustomerJSON = async (
  siteShortName: string,
  customerId: string | null
): Promise<CustomerJson> => {
  if (!customerId) {
    return Promise.reject();
  }

  const API_PROXY_PATH = getAPIProxyPath(siteShortName);

  let targetUrl = `${API_PROXY_PATH}/api/v2/customers/${customerId}`;

  if (Environment.DesignerMode.PREVIEW) {
    targetUrl += `?preview=${Environment.PREVIEW_ID}`;
  }

  return fetch(targetUrl).then((response) => response.json());
};

export const fetchSiteOptimizeAnalyzeData = async (
  siteShortName: string
): Promise<OptimizationJsonSiteOptimizeAnalyze | undefined> => {
  try {
    let targetUrl = `/api/sites/${siteShortName}/optimize-analyze`;

    if (Environment.DesignerMode.PREVIEW) {
      targetUrl += `?preview=${Environment.PREVIEW_ID}`;
    }

    const res = await fetch(targetUrl);
    return await res.json();
  } catch {
    //  We don't want this to block core Optimize UI/UX so treat errors here as a no-op
    //  We'll handle the return of undefined as a catch-all error case to be shown in the UI
  }
  return undefined;
};

export const fetchOptimizationJson = async (
  siteShortName: string,
  customerId: string | null
): Promise<OptimizationJson> => {
  const [customerJson, siteOptimizeAnalyzeData] = await Promise.all([
    await fetchIntellimizeCustomerJSON(siteShortName, customerId),
    await fetchSiteOptimizeAnalyzeData(siteShortName),
  ]);

  // Populate rawCustomerJson cache to avoid duplicate fetches
  // This allows optimization-ui shared components to read from cache without refetching
  // setQueryData both updates cache AND notifies subscribers (triggers re-renders)
  if (customerId) {
    queryClient.setQueryData(
      queryKeys.rawCustomerJson({siteShortName}),
      customerJson
    );
  }

  return customerJsonToOptimizationJson(customerJson, siteOptimizeAnalyzeData);
};

export const invalidateOptimizationJsonQuery = (
  siteShortName: string,
  dispatch?: (action: OptimizationJsonInvalidatedAction) => void
) => {
  dispatch?.(optimizationJsonInvalidated());
  return queryClient.invalidateQueries(
    queryKeys.optimizationJson({
      siteShortName,
    })
  );
};

export const fetchOptimizationJsonQuery = ({
  customerId,
  siteShortName,
}: {
  customerId: string | null;
  siteShortName: string;
}) =>
  queryClient.fetchQuery({
    queryKey: queryKeys.optimizationJson({siteShortName}),
    queryFn: () => fetchOptimizationJson(siteShortName, customerId),
  });

const _selectCurrentExperienceId = createSelector(
  selectOptimizationStore,
  selectCurrentExperienceId
);

const maybeIncludeTemporaryExperience = (
  currentExperienceId: string | null,
  currentPageId: string | null,
  pageStore: PageState
): ((optimizationJson: OptimizationJson) => OptimizationJson) => {
  return (optimizationJson: OptimizationJson) => {
    // Only include temporary experience if it's the current experience ID
    if (
      !optimizationJson ||
      currentExperienceId !== TEMPORARY_EXPERIENCE_ID ||
      !currentPageId
    ) {
      return optimizationJson;
    }

    // Check if temporary experience already exists
    if (optimizationJson.experiences[TEMPORARY_EXPERIENCE_ID]) {
      return optimizationJson;
    }

    // Get current page name
    const currentPage = getPage(currentPageId, pageStore);
    const pageName = currentPage?.name || 'Page';

    // Generate a unique name for the temporary experience based on page name
    const existingNames = Object.values(optimizationJson.experiences).map(
      (exp) => exp.name
    );
    const experienceName = generateNewExperienceName(pageName, existingNames);

    // Add temporary experience with generated name
    const temporaryExperience = createTemporaryExperience(experienceName);

    return {
      ...optimizationJson,
      experiences: {
        ...optimizationJson.experiences,
        [TEMPORARY_EXPERIENCE_ID]: temporaryExperience,
      },
    };
  };
};

export const _useOptimizationJson = (
  useDispatch: UseDispatch,
  useDesignerSelector: UseDesignerSelector
) => {
  return function useOptimizationJson() {
    const currentExperienceId = useDesignerSelector(_selectCurrentExperienceId);
    const currentPageId = useDesignerSelector(selectCurrentPageId);
    const pageStore = useDesignerSelector(getPageStore);
    const siteShortName = useDesignerSelector(selectSiteShortName);
    const customerId = useDesignerSelector(selectCustomerId);

    const {entitlements} = useEntitlements();
    const hasOptimizeOrAnalyze = Boolean(
      entitlements?.allowOptimizeCore?.hasAccess ||
      entitlements?.allowAnalyzeCore?.hasAccess
    );
    // Campaigns Home is not page-build/edit mode, so isInCampaignBuildMode is
    // false there. campaignBuilderAbility covers Campaigns Integrations hosts
    // that still need customer JSON without Optimize/Analyze entitlements.
    const hasCampaignBuilderAbility = useDesignerSelector(
      selectDesignerAbility('campaignBuilderAbility')
    );

    const query = useQuery({
      queryKey: queryKeys.optimizationJson({siteShortName}),
      queryFn: () => fetchOptimizationJson(siteShortName, customerId),
      enabled: Boolean(
        (hasOptimizeOrAnalyze || hasCampaignBuilderAbility) &&
        siteShortName &&
        customerId
      ),
      retry: false,
      staleTime: Infinity,
      select: maybeIncludeTemporaryExperience(
        currentExperienceId,
        currentPageId,
        pageStore
      ),
    });

    return query;
  };
};
